import { useEffect, useState } from 'react';
import useFetchCurrentUserData from '../../../hooks/useFetchCurrentUserData';
import { useCommonUIActionsContext } from '../../../contexts/CommonUIActionsContext';
import { RenderOn } from '../../../lib/RenderOn/RenderOn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { USER_ENTOURAGE_QUERY_KEY } from '@r-and-a-shared-ui/common-ui/src/constants';
import {
  faPhone,
  faEnvelope,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { UserRole } from '@r-and-a-shared-ui/utils';

import { getPPContactToDisplay } from '@r-and-a-shared-ui/common-ui';

import styles from './PlayerPortalsIDCard.module.scss';

type UserEntourage = {
  assignedCaddies?: string[] | undefined;
  assignedManagers?: string[] | undefined;
  assignedPlayers?: string[] | undefined;
};

type Contact = {
  role: { value: UserRole };
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  entourage: any[];
};

export type PlayerPortalsIDCardProps = {
  greetingMessage?: string;
  playerLabel?: string;
  managerLabel?: string;
  caddieLabel?: string;
  playersNrLabel?: string;
  resource: string;
  theme?: string;
  roles?: string[];
  playerContactId?: string;
  kontentItemId?: string;
};

const groupUserEntourage = (contact: Contact) => {
  const userRole = contact?.role?.value;
  const entourage = contact?.entourage ?? [];

  if (userRole === UserRole.Player || userRole === UserRole.LCQPlayer) {
    return {
      assignedManagers: entourage
        .filter(
          member =>
            member?.role.value === UserRole.Manager ||
            member?.role.value === UserRole.LCQManager,
        )
        .map(member => member.name),
      assignedCaddies: entourage
        .filter(
          member =>
            member?.role.value === UserRole.Caddie ||
            member?.role.value === UserRole.LCQCaddie,
        )
        .map(member => member.name),
    };
  }

  if (userRole === UserRole.Manager || userRole === UserRole.LCQManager) {
    return {
      assignedPlayers: entourage.map(member => member.name),
    };
  }

  if (userRole === UserRole.Caddie || userRole === UserRole.LCQCaddie) {
    return {
      assignedPlayers: entourage.map(member => member.name),
    };
  }

  return {};
};

export const PlayerPortalsIDCard = (props: PlayerPortalsIDCardProps) => {
  const {
    greetingMessage,
    playerLabel,
    managerLabel,
    caddieLabel,
    playersNrLabel,
    theme = 'light',
    playerContactId,
    kontentItemId,
  } = props;

  const [userContact, setUserContact] = useState<Contact | null>(null);
  const [groupedUserEntourage, setUserEntourage] =
    useState<UserEntourage | null>(null);
  const context = useCommonUIActionsContext();

  const { userDataAPI } = context || {};
  const getContactAndEntouragePath =
    process.env.NEXT_PUBLIC_GET_CONTACT_ENTOURAGE_BY_ID_PATH!;

  const priorityContact = getPPContactToDisplay();

  const { data, loading, hasFetched } = useFetchCurrentUserData(
    getContactAndEntouragePath,
    userDataAPI,
    USER_ENTOURAGE_QUERY_KEY,
    playerContactId ?? priorityContact?.id,
  );

  useEffect(() => {
    if (data?.contact) {
      const contact = data.contact as Contact;
      const groupedUserEntourage = groupUserEntourage(contact);

      setUserContact(contact);
      setUserEntourage(groupedUserEntourage);
    }
  }, [data]);

  return (
    <RenderOn condition={hasFetched && !loading && userContact !== null}>
      <div
        className={`${styles.wrapper} ${styles[theme]}`}
        data-kontent-item-id={kontentItemId}
      >
        <h1 className={styles.title}>
          {greetingMessage}
          {userContact?.name}
        </h1>
        <div className={styles.background}>
          <span> {userContact?.firstName} </span>
          <span>{userContact?.lastName}</span>
        </div>
        <div className={styles.userContact}>
          {userContact?.phoneNumber && (
            <span className={styles.phoneNumber}>
              <FontAwesomeIcon icon={faPhone} className={styles.icon} />
              {userContact?.phoneNumber}
            </span>
          )}
          {userContact?.email && (
            <span className={styles.email}>
              <FontAwesomeIcon icon={faEnvelope} className={styles.icon} />
              {userContact?.email}
            </span>
          )}
        </div>

        <div className={styles.userTeam}>
          {(userContact?.role?.value === UserRole.Player ||
            userContact?.role?.value === UserRole.LCQPlayer) && (
            <>
              {groupedUserEntourage?.assignedManagers &&
                groupedUserEntourage?.assignedManagers?.length > 0 && (
                  <div className={styles.entourageGroup}>
                    <span className={styles.label}>{managerLabel} </span>
                    {groupedUserEntourage?.assignedManagers?.map(name => (
                      <span key={name}>{name}</span>
                    ))}
                  </div>
                )}

              {groupedUserEntourage?.assignedCaddies &&
                groupedUserEntourage?.assignedCaddies?.length > 0 && (
                  <div className={styles.entourageGroup}>
                    <span className={styles.label}>{caddieLabel} </span>
                    {groupedUserEntourage?.assignedCaddies?.map(name => (
                      <span key={name}>{name}</span>
                    ))}
                  </div>
                )}
            </>
          )}

          {(userContact?.role?.value === UserRole.Manager ||
            userContact?.role?.value === UserRole.LCQManager) && (
            <div>
              <div className={styles.managerPlayers}>
                <FontAwesomeIcon icon={faUsers} className={styles.icon} />
                <span>
                  {groupedUserEntourage?.assignedPlayers &&
                    groupedUserEntourage.assignedPlayers.length}{' '}
                  {playersNrLabel}
                </span>
              </div>

              {groupedUserEntourage?.assignedPlayers &&
                groupedUserEntourage?.assignedPlayers.map(name => (
                  <ul key={name}>
                    <li>{name}</li>
                  </ul>
                ))}
            </div>
          )}

          {(userContact?.role?.value === UserRole.Caddie ||
            userContact?.role?.value === UserRole.LCQCaddie) && (
            <>
              {groupedUserEntourage?.assignedPlayers &&
                groupedUserEntourage?.assignedPlayers?.length > 0 && (
                  <div className={styles.entourageGroup}>
                    <span className={styles.label}>{playerLabel} </span>
                    {groupedUserEntourage?.assignedPlayers?.map(name => (
                      <span key={name}>{name}</span>
                    ))}
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </RenderOn>
  );
};

export default PlayerPortalsIDCard;
